import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Search, ShieldCheck, MapPin, Heart } from "lucide-react";
import { Badge } from "../components/ui/badge";

export default function RescuesDirectory() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [region, setRegion] = useState("");
    useEffect(() => { api.get("/rescues").then((r) => setItems(r.data)); }, []);
    const regions = useMemo(() => Array.from(new Set(items.map((i) => i.region).filter(Boolean))).sort(), [items]);
    const filt = useMemo(() => items.filter((i) => {
        if (region && i.region !== region) return false;
        if (q && !`${i.name} ${i.description} ${i.location}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    }), [items, q, region]);

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="rescues-directory">
            <div className="mb-8">
                <div className="eyebrow mb-3">Partner rescues · Worldwide</div>
                <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">The rescuers making it happen.</h1>
            </div>
            <div className="relative max-w-md mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rl-text-3)]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rescues…"
                       className="pl-9 border-[var(--rl-border)] h-11 rounded-full" data-testid="rescues-search" />
            </div>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="rescues-region-filter">
                <button onClick={() => setRegion("")} className={`px-4 py-1.5 rounded-full text-sm border ${region === "" ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>All regions</button>
                {regions.map((r) => (
                    <button key={r} onClick={() => setRegion(r)} data-testid={`region-chip-${r.replace(/\s+/g, "-")}`}
                            className={`px-4 py-1.5 rounded-full text-sm border ${region === r ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>{r}</button>
                ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filt.map((r) => (
                    <div key={r.id} className="card-warm rounded-xl overflow-hidden" data-testid={`rescue-card-${r.id}`}>
                        <div className="aspect-[16/9] bg-[var(--rl-cream)] overflow-hidden">
                            <img src={r.logo_url} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="p-5">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-serif text-xl">{r.name}</h3>
                                {r.verified && <ShieldCheck className="w-4 h-4 text-[var(--rl-success)]" />}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-[var(--rl-text-3)] mb-3"><MapPin className="w-3 h-3" /> {r.location}<span className="ml-1 text-[10px] uppercase tracking-widest bg-[var(--rl-cream)] px-2 py-0.5 rounded-full">{r.region}</span></div>
                            <p className="text-sm text-[var(--rl-text-2)] line-clamp-3">{r.description}</p>
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {r.focus.map((f) => <Badge key={f} variant="outline" className="text-[10px] uppercase tracking-widest">{f}</Badge>)}
                            </div>
                            <div className="mt-4 pt-4 border-t border-[var(--rl-border)] flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-sm"><Heart className="w-4 h-4 text-[var(--rl-primary)]" /> <span className="font-semibold">{r.dogs_saved.toLocaleString()}</span> <span className="text-[var(--rl-text-2)]">saved</span></div>
                                {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[var(--rl-primary)] hover:underline">Visit site →</a>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
